/* Control de progreso — ejecución (Fase 5A). */
import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { avanzoApi } from "@/lib/avanzoApi";
import PaesImage from "@/components/PaesImage";
import PostEvalBuddy from "./PostEvalBuddy";

const SUBJECT_LABELS = {
  matematica: "Precálculo",
  sociales:   "Ciudadanía y Valores",
  ciencias:   "Ciencia y Tecnología",
  lenguaje:   "Lenguaje y Literatura",
};

export default function AvanzoProgressCheck() {
  const [status, setStatus]   = useState(null);
  const [current, setCurrent] = useState(null);
  const [result, setResult]   = useState(null);
  const [selected, setSelected] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [phase, setPhase]     = useState("loading"); // loading|locked|intro|run|done
  const [confirmStart, setConfirmStart] = useState(false);
  const navigate = useNavigate();

  const refresh = useCallback(async () => {
    try {
      const st = (await avanzoApi.progressStatus()).data;
      setStatus(st);
      if (st.has_active_run) {
        try {
          const cur = (await avanzoApi.progressCurrent()).data;
          setCurrent(cur);
          setPhase("run");
        } catch { setPhase("intro"); }
      } else if (st.last_run_id) {
        try {
          const res = (await avanzoApi.progressResult()).data;
          setResult(res);
          setPhase("done");
        } catch { setPhase(st.gate.unlocked ? "intro" : "locked"); }
      } else {
        setPhase(st.gate.unlocked ? "intro" : "locked");
      }
    } catch (e) {
      setPhase("locked");
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const handleStart = async () => {
    try {
      await avanzoApi.progressStart();
      await refresh();
    } catch (e) {
      alert("No se pudo iniciar el Control de progreso.");
    }
  };

  const handleAnswer = async () => {
    if (!selected || submitting) return;
    setSubmitting(true);
    try {
      const r = await avanzoApi.progressAnswer({
        run_id:  current.run_id,
        item_id: current.item.item_id,
        selected_answer: selected,
      });
      if (r.data.completed) {
        const res = (await avanzoApi.progressResult()).data;
        setResult(res); setPhase("done");
      } else {
        const nxt = (await avanzoApi.progressCurrent()).data;
        setCurrent(nxt); setSelected(null);
      }
    } catch (e) { /* swallow to keep UX calm */ }
    setSubmitting(false);
  };

  if (phase === "loading") {
    return <div className="avz-empty" data-testid="avz-pc-loading">Cargando…</div>;
  }

  if (phase === "locked") {
    const g = status?.gate || {};
    return (
      <div className="avz-card" data-testid="avz-pc-locked">
        <h2 className="avz-h1">Control de progreso</h2>
        <p className="avz-body">
          Aún no está disponible. Necesitas al menos <strong>6 sesiones</strong> de
          entrenamiento en <strong>3 materias distintas</strong>.
        </p>
        <p className="avz-small" data-testid="avz-pc-gate-stats">
          Llevas {g.sessions ?? 0} sesión(es) en {g.subjects_practiced ?? 0} materia(s).
        </p>
        <button className="avz-btn"
                data-testid="avz-pc-back-to-training"
                onClick={() => navigate("/avanzo/entrenar")}>
          Continuar entrenando
        </button>
      </div>
    );
  }

  if (phase === "intro") {
    return (
      <div className="avz-card" data-testid="avz-pc-intro">
        <h2 className="avz-h1">Tu Control de progreso está listo</h2>
        <p className="avz-body">
          Responderás <strong>16 actividades</strong>, 4 por cada área.
          No necesitas estudiar algo nuevo antes de comenzar. Queremos observar
          cuánto has avanzado con tu preparación.
        </p>
        <ul className="avz-list avz-mt-4">
          <li>16 actividades</li>
          <li>Aproximadamente 20–25 minutos</li>
        </ul>
        {!confirmStart ? (
          <button className="avz-btn avz-btn-primary avz-mt-4"
                  data-testid="avz-pc-cta-start"
                  onClick={() => setConfirmStart(true)}>
            Comenzar mi Control de progreso
          </button>
        ) : (
          <div className="avz-mt-4" data-testid="avz-pc-confirm">
            <p className="avz-body">
              Procura hacerlo en un momento en el que puedas concentrarte.
            </p>
            <button className="avz-btn avz-btn-primary avz-mt-2"
                    data-testid="avz-pc-confirm-start"
                    onClick={handleStart}>
              Estoy listo, iniciar
            </button>
          </div>
        )}
      </div>
    );
  }

  if (phase === "run" && current) {
    const it = current.item;
    return (
      <div className="avz-card" data-testid="avz-pc-run">
        <div className="avz-small" data-testid="avz-pc-progress">
          Actividad {current.index} de {current.total}
        </div>
        {it.contexto_estimulo && (
          <div className="avz-context avz-mt-2"
               data-testid="avz-pc-context">
            {it.contexto_estimulo}
          </div>
        )}
        <PaesImage item={it} />
        <h3 className="avz-h2 avz-mt-4" data-testid="avz-pc-question">
          {it.pregunta}
        </h3>
        <div className="avz-options avz-mt-4">
          {["A", "B", "C", "D"].map(L => (
            <label key={L}
                   className={`avz-option ${selected === L ? "avz-option-active" : ""}`}
                   data-testid={`avz-pc-option-${L}`}>
              <input type="radio"
                     name="pc-answer"
                     value={L}
                     checked={selected === L}
                     onChange={() => setSelected(L)}
                     disabled={submitting} />
              <span className="avz-option-letter">{L}</span>
              <span className="avz-option-text">{it[`opcion_${L}`]}</span>
            </label>
          ))}
        </div>
        <button className="avz-btn avz-btn-primary avz-mt-4"
                data-testid="avz-pc-submit"
                disabled={!selected || submitting}
                onClick={handleAnswer}>
          {submitting ? "Enviando…" :
            current.index === current.total ? "Finalizar" : "Siguiente"}
        </button>
      </div>
    );
  }

  if (phase === "done" && result) {
    return (
      <div className="avz-card" data-testid="avz-pc-result">
        <h2 className="avz-h1">Tu progreso hasta ahora</h2>
        <div className="avz-hero avz-mt-4" data-testid="avz-pc-band">
          {result.band_label}
        </div>
        <p className="avz-small">
          {result.total_correct} de {result.total_items} — {result.percentage}%
        </p>
        <div className="avz-mt-6" data-testid="avz-pc-by-subject">
          <h3 className="avz-h2">Cómo te fue por materia</h3>
          {Object.entries(result.subject_results).map(([s, r]) => (
            <div
              key={s}
              className="avz-row-between"
              data-testid={`avz-pc-subject-${s}`}
              style={{
                padding: "10px 12px",
                borderRadius: 10,
                marginTop: 8,
                borderLeft: `4px solid var(--avz-subject-${s})`,
                background: "#f9fbfd",
              }}
            >
              <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span
                  aria-hidden="true"
                  style={{
                    width: 10, height: 10, borderRadius: 999,
                    background: `var(--avz-subject-${s})`,
                    display: "inline-block",
                  }}
                />
                {SUBJECT_LABELS[s] || s}
              </span>
              <span><strong>{r.correct}</strong> de {r.total}</span>
            </div>
          ))}
        </div>
        {result.areas_to_review?.length > 0 && (
          <div className="avz-mt-4" data-testid="avz-pc-review">
            <h3 className="avz-h2">Áreas para seguir practicando</h3>
            <ul>
              {result.areas_to_review.map(s => (
                <li key={s}>{SUBJECT_LABELS[s] || s}</li>
              ))}
            </ul>
          </div>
        )}
        <PostEvalBuddy source="progress_check" reflectionType="POST_PC" />
        <button className="avz-btn avz-btn-primary avz-mt-6"
                data-testid="avz-pc-continue"
                onClick={() => navigate("/avanzo/entrenar")}>
          Continuar mi preparación
        </button>
      </div>
    );
  }

  return <div>—</div>;
}

